/**
 * Shared localStorage auth helpers — single source of truth for auth keys.
 * Used by login/page.tsx (finishLogin), auth.tsx (logout, tryRefresh),
 * and api.ts (401 refresh failure) to avoid 3× duplicated logic.
 *
 * NOTE: The JWT access token itself is stored in an HttpOnly cookie
 * (not accessible to JS). We only store metadata in localStorage:
 * - tokenExp: expiry timestamp (ms) for proactive refresh
 * - impersonatedBy: admin email if impersonating
 */

const AUTH_KEYS = [
  'token',
  'tokenExp',
  'impersonatedBy',
  'userId',
  'email',
  'role',
  'roles',
  'permissions',
  'firstName',
  'lastName',
  'adminBackup',
] as const;

/** Remove all auth-related keys from localStorage and emit 'storage' event. */
export function clearAuthStorage(): void {
  if (typeof window === 'undefined') return;
  for (const key of AUTH_KEYS) {
    window.localStorage.removeItem(key);
  }
  window.dispatchEvent(new Event('storage'));
}

/**
 * Module-level flag: set by finishLogin() to prevent the "already authenticated"
 * effects in Layout.tsx and login/page.tsx from overriding the redirect.
 *
 * Flow without this flag:
 *   1. finishLogin() calls persistAuthStorage() → setIsAuthenticated(true)
 *   2. finishLogin() reads redirectAfterLogin, removes it, calls router.push(redirect)
 *   3. React re-renders → Layout's "already authenticated" effect runs
 *   4. Effect finds redirectAfterLogin is null → calls router.replace('/')
 *   5. router.replace('/') overrides router.push(redirect) → user lands on home page
 *
 * With this flag:
 *   1. finishLogin() sets loginRedirectHandled = true
 *   2. finishLogin() calls persistAuthStorage() → setIsAuthenticated(true)
 *   3. finishLogin() reads redirectAfterLogin, removes it, calls router.push(redirect)
 *   4. React re-renders → Layout's effect checks isLoginRedirectHandled() → true → skip
 *   5. router.push(redirect) completes → user lands on correct page
 */
let loginRedirectHandled = false;

export function isLoginRedirectHandled(): boolean {
  return loginRedirectHandled;
}

export function setLoginRedirectHandled(value: boolean): void {
  loginRedirectHandled = value;
}

/** Auth response shape from /api/auth/login, /api/auth/refresh, /api/auth/verify-2fa. */
export type AuthResponseData = {
  token: string;
  tokenExp?: number | null;
  impersonatedBy?: string | null;
  id?: number;
  email: string;
  role: string;
  roles?: string[];
  permissions?: string[];
  firstName?: string | null;
  lastName?: string | null;
  mustChangePassword?: boolean;
};

/** Persist auth response metadata to localStorage and emit 'storage' event.
 *  The JWT itself is set as an HttpOnly cookie by the backend — we only
 *  store tokenExp + impersonatedBy + user info here. */
export function persistAuthStorage(data: AuthResponseData): void {
  if (typeof window === 'undefined') return;
  // Clean up legacy token in localStorage (from before HttpOnly cookie migration)
  window.localStorage.removeItem('token');
  if (data.tokenExp) window.localStorage.setItem('tokenExp', String(data.tokenExp));
  else window.localStorage.removeItem('tokenExp');
  if (data.impersonatedBy) window.localStorage.setItem('impersonatedBy', data.impersonatedBy);
  else window.localStorage.removeItem('impersonatedBy');
  if (data.id) window.localStorage.setItem('userId', data.id.toString());
  else window.localStorage.removeItem('userId');
  window.localStorage.setItem('email', data.email);
  window.localStorage.setItem('role', data.role);
  if (data.roles) window.localStorage.setItem('roles', JSON.stringify(data.roles));
  else window.localStorage.removeItem('roles');
  if (data.permissions) window.localStorage.setItem('permissions', JSON.stringify(data.permissions));
  else window.localStorage.removeItem('permissions');
  if (data.firstName) window.localStorage.setItem('firstName', data.firstName);
  else window.localStorage.removeItem('firstName');
  if (data.lastName) window.localStorage.setItem('lastName', data.lastName);
  else window.localStorage.removeItem('lastName');
  if (data.mustChangePassword) window.localStorage.setItem('mustChangePassword', '1');
  else window.localStorage.removeItem('mustChangePassword');
  window.dispatchEvent(new Event('storage'));
}

/** true = uživatel musí při nejbližší příležitosti změnit heslo (nastavil admin). */
export function isMustChangePassword(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem('mustChangePassword') === '1';
}

/** Zruší flag vynucené změny hesla (po úspěšné změně). */
export function clearMustChangePassword(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem('mustChangePassword');
}
